// src/pages/PrivacyPolicyPage.js
import React from 'react';
import Navbar from '../components/common/Navbar/Navbar';
import Footer from '../components/common/Footer/Footer';
import './PolicyPages.css';

const PrivacyPolicyPage = () => {
    return (
        <div className="policy-page">
            <Navbar />

            <div className="policy-header">
                <div className="container">
                    <h1>Privacy Policy</h1>
                    <p className="last-updated">Last Updated: January 27, 2026</p>
                </div>
            </div>

            <div className="policy-content">
                <div className="container">
                    <div className="policy-container">
                        <div className="policy-introduction">
                            <p>
                                At Abhyaas Educational Trust, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
                            </p>
                        </div>

                        <div className="policy-section">
                            <h2>Information We Collect</h2>
                            <p>We may collect the following types of information:</p>
                            <ul>
                                <li>
                                    <strong>Personal Information:</strong> Name, email address, phone number, and other contact information provided when you register for programs, events, or inquire about our services.
                                </li>
                                <li>
                                    <strong>Child Information:</strong> Basic information about your child such as name, age, and interests when you enroll them in our programs. This information is always collected with parental consent.
                                </li>
                                <li>
                                    <strong>Usage Information:</strong> Information about how you use our website, including IP address, browser type, pages visited, and time spent on our site.
                                </li>
                            </ul>
                        </div>

                        <div className="policy-section">
                            <h2>How We Use Your Information</h2>
                            <p>We use the information we collect for various purposes, including:</p>
                            <ul>
                                <li>Providing, maintaining, and improving our educational services</li>
                                <li>Processing enrollments, registrations, and payments</li>
                                <li>Communicating with you about programs, events, and updates</li>
                                <li>Responding to your inquiries and providing customer support</li>
                                <li>Analyzing website usage to enhance user experience</li>
                                <li>Complying with legal obligations</li>
                            </ul>
                        </div>

                        <div className="policy-section">
                            <h2>Information Sharing</h2>
                            <p>We do not sell or rent your personal information to third parties. We may share information in the following circumstances:</p>
                            <ul>
                                <li>With service providers who help us operate our website and provide services</li>
                                <li>When required by law or to protect our rights</li>
                                <li>In the event of a merger, acquisition, or sale of assets (with appropriate safeguards)</li>
                            </ul>
                        </div>

                        <div className="policy-section">
                            <h2>Data Security</h2>
                            <p>
                                We implement appropriate technical and organizational measures to protect your data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, so we cannot guarantee absolute security.
                            </p>
                        </div>

                        <div className="policy-section">
                            <h2>Your Rights</h2>
                            <p>Depending on your location, you may have the right to:</p>
                            <ul>
                                <li>Access personal information we hold about you</li>
                                <li>Correct inaccurate or incomplete information</li>
                                <li>Request deletion of your personal information</li>
                                <li>Withdraw consent where processing is based on consent</li>
                                <li>Object to processing of your information</li>
                            </ul>
                        </div>

                        <div className="policy-section">
                            <h2>Children's Privacy</h2>
                            <p>
                                We are committed to protecting children's privacy. We collect information about children only with parental consent and use it solely for educational purposes. Parents can review, update, or request deletion of their child's information at any time.
                            </p>
                        </div>

                        {/* Abhyaas Preschool App Section */}
                        <div className="policy-section app-privacy-section">
                            <h2>Abhyaas Preschool App - Privacy Policy</h2>
                            <p>
                                This section specifically covers the privacy practices for the <strong>Abhyaas Preschool App</strong> (also known as "Abhyaas - Parent Portal"), a mobile application available on iOS (iPhone and iPad) and Android devices.
                            </p>

                            <h3>About the App</h3>
                            <p>
                                The Abhyaas Preschool App is developed and maintained by <strong>Neuralway Technologies Private Limited</strong> on behalf of Abhyaas Educational Trust. The app serves as a dedicated parent portal, enabling parents to stay connected with their child's preschool activities, view photos and videos, receive school notifications, and track events.
                            </p>
                            <ul>
                                <li><strong>App Name:</strong> Abhyaas Preschool / Abhyaas - Parent Portal</li>
                                <li><strong>Developer:</strong> Neuralway Technologies Private Limited</li>
                                <li><strong>Platforms:</strong> iOS (iPhone & iPad), Android</li>
                                <li><strong>Bundle/Package ID:</strong> com.neuralway.abhyaas.mobile</li>
                                <li><strong>Category:</strong> Education</li>
                            </ul>

                            <h3>Information Collected by the App</h3>
                            <p>When you use the Abhyaas Preschool App, we may collect the following information:</p>

                            <h4>Account & Authentication Data</h4>
                            <ul>
                                <li>Email address (used as your unique identifier)</li>
                                <li>Full name</li>
                                <li>Phone number (optional)</li>
                                <li>Password (securely hashed and encrypted)</li>
                                <li>Login activity and timestamps</li>
                            </ul>

                            <h4>Child Information</h4>
                            <ul>
                                <li>Child's name</li>
                                <li>Child's class/grade level</li>
                                <li>Child ID (for internal reference)</li>
                            </ul>

                            <h4>Usage & Activity Data</h4>
                            <ul>
                                <li>App usage patterns (screens visited, features used)</li>
                                <li>Media interactions (photos/videos viewed, liked)</li>
                                <li>Notification read status</li>
                                <li>Event participation records</li>
                                <li>Session information</li>
                            </ul>

                            <h4>Device Information</h4>
                            <ul>
                                <li>Device type and operating system</li>
                                <li>App version</li>
                                <li>Push notification tokens (for sending notifications)</li>
                            </ul>

                            <h3>App Permissions</h3>
                            <p>The Abhyaas Preschool App requests the following device permissions:</p>

                            <h4>iOS (iPhone & iPad)</h4>
                            <ul>
                                <li><strong>Camera:</strong> To capture and share photos of your child's activities with teachers</li>
                                <li><strong>Photo Library:</strong> To select and share photos, and to save school event media to your device</li>
                                <li><strong>Face ID:</strong> For quick and secure sign-in to protect your family's information</li>
                                <li><strong>Notifications:</strong> To receive important school announcements and updates</li>
                            </ul>

                            <h4>Android</h4>
                            <ul>
                                <li><strong>Camera:</strong> To capture and share photos</li>
                                <li><strong>Biometric/Fingerprint:</strong> For secure authentication</li>
                                <li><strong>Media Access:</strong> To access and save photos and videos</li>
                                <li><strong>Notifications:</strong> To receive school announcements and alerts</li>
                            </ul>

                            <h3>How the App Uses Your Information</h3>
                            <ul>
                                <li>Display your child's school photos, videos, and activity updates</li>
                                <li>Send push notifications about school events, announcements, and alerts</li>
                                <li>Enable secure login and authentication</li>
                                <li>Facilitate communication between parents and the school</li>
                                <li>Track event participation and engagement</li>
                                <li>Improve app performance and user experience</li>
                            </ul>

                            <h3>Third-Party Services</h3>
                            <p>The Abhyaas Preschool App uses the following third-party services to provide its functionality:</p>
                            <ul>
                                <li><strong>Amazon Web Services (AWS):</strong> Cloud infrastructure for secure data storage, processing, and content delivery (including AWS Lambda, DynamoDB, S3, and CloudFront)</li>
                                <li><strong>Expo:</strong> Mobile app development and update distribution platform</li>
                            </ul>
                            <p>
                                These services are used solely to operate the app and are bound by their respective privacy policies and data protection agreements. We do not share your personal information with third parties for marketing purposes.
                            </p>

                            <h3>Data Storage & Security</h3>
                            <p>
                                All data collected through the Abhyaas Preschool App is stored securely using industry-standard encryption. Passwords are hashed using bcrypt encryption. Sensitive credentials on your device are stored using secure storage mechanisms (iOS Keychain / Android Keystore). All data transmission occurs over encrypted HTTPS connections.
                            </p>

                            <h3>Data Retention</h3>
                            <p>
                                Your account data is retained for as long as your child is enrolled at the school and your account remains active. Media content (photos and videos) may be retained for archival purposes. You may request deletion of your account and associated data at any time by contacting us.
                            </p>

                            <h3>Your Rights Regarding App Data</h3>
                            <p>As a user of the Abhyaas Preschool App, you have the right to:</p>
                            <ul>
                                <li>Access your personal data stored in the app</li>
                                <li>Update or correct your account information</li>
                                <li>Request deletion of your account and data</li>
                                <li>Opt-out of non-essential notifications</li>
                                <li>Disable biometric authentication at any time</li>
                            </ul>

                            <h3>App Support Contact</h3>
                            <p>
                                For questions, concerns, or requests specifically related to the Abhyaas Preschool App, please contact:
                            </p>
                            <div className="contact-info">
                                <p>Email: <a href="mailto:zero@neuralway.ai">zero@neuralway.ai</a></p>
                                <p>Developer: Neuralway Technologies Private Limited</p>
                            </div>
                        </div>

                        <div className="policy-section">
                            <h2>Changes to This Policy</h2>
                            <p>
                                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                            </p>
                        </div>

                        <div className="policy-section">
                            <h2>Contact Us</h2>
                            <p>
                                If you have any questions or concerns about this Privacy Policy, please contact us at:
                            </p>
                            <div className="contact-info">
                                <p>Email: <a href="mailto:abhyaas@littleelly.com">abhyaas@littleelly.com</a></p>
                                <p>Phone: +91 90198 29264</p>
                                <p>Address: Little Elly Campus, Kudlu Main Road, Bengaluru, Karnataka 560068</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default PrivacyPolicyPage;